import { ReactNode, useEffect } from "react";
import { IPaginationFilter } from "./Tables/PaginationWithFilters";

export interface IButtonGroupOption {
  id: string;
  label: string;
  value: string | number | boolean;
  disabled?: boolean;
  onClick?: () => void; // For action mode
  isLoading?: boolean; // For action mode
  customContent?: ReactNode; // For custom content like loading spinners
}

export interface IButtonGroupProps {
  options: IButtonGroupOption[];
  selectedIndex?: number | null | undefined; // Optional for action mode
  onChange?: (id: string) => void; // Optional for action mode
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  actionMode?: boolean; // If true, uses onClick from options instead of onChange
}

export default function ButtonGroup({
  options,
  selectedIndex,
  onChange,
  disabled = false,
  size = "md",
  actionMode = false,
  
}: IButtonGroupProps) {
  const sizeClasses = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };

  return (
    <div className="inline-flex rounded-md shadow-sm border border-gray-300 overflow-hidden" role="group">
      {options.map((option, index) => {
        const isActive = !actionMode && selectedIndex !== null && selectedIndex !== undefined && selectedIndex === index;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const isDisabled = disabled || option.disabled || option.isLoading;

        // console.log("ButtonGroup Option:", option, "isActive:", isActive, "isDisabled:", isDisabled, "Value:", value);

        const handleClick = () => {
          if (isDisabled) return;
          if (actionMode && option.onClick) {
            option.onClick();
          } else if (!actionMode && onChange) {
            onChange(option.id);
          }
        };

        return (
          <button
            key={index}
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            className={`
              ${sizeClasses[size]}
              font-medium
              transition-colors
              whitespace-nowrap
              max-w-none
              min-w-fit
              border-r border-gray-300
              ${isLast ? "border-r-0" : ""}
              ${isFirst ? "rounded-l-md" : "rounded-none"}
              ${isLast ? "rounded-r-md" : "rounded-none"}
              ${option.isLoading
                ? "bg-primary-600 text-white border-primary-600 opacity-50 cursor-not-allowed"
                : isActive
                ? "bg-primary-600 text-white border-primary-600 z-10"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
              ${isDisabled && !option.isLoading
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer"
              }
              focus:z-10 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              ${option.customContent ? "flex items-center justify-center" : ""}
            `}
          >
            {option.customContent || option.label}
          </button>
        );
      })}
    </div>
  );
}
